import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn, Modal, ExportButton } from "@/components/common";
import { downloadCSV, today } from "@/lib/csv";
import { MONTHS } from "@/lib/calendarUtils";
import BookingForm from "@/components/BookingForm";
import InvoiceForm from "@/components/InvoiceForm";
import { draftFromBookings } from "@/lib/invoice";
import { Plus, Pencil, Trash2, Wallet, FileText, ReceiptText, Send, Mail, MessageCircle, Ban, AlertTriangle, CalendarClock, RotateCcw, FileSpreadsheet } from "lucide-react";

const PAYMENT = { conto_corrente: "Conto corrente", cash: "Cash" };
const CANCEL_REASONS = [
  ["prima_60", "Prima di 60 giorni (voucher 100%)"],
  ["entro_60", "Entro 60 giorni (voucher 70%)"],
  ["entro_15", "Entro 15 giorni (voucher 50%)"],
  ["force_majeure", "Force Majeure (voucher 100%)"],
  ["no_show", "No Show (0%)"],
];
const CANCEL_LABEL = Object.fromEntries(CANCEL_REASONS);

export default function Money() {
  const { isAdmin, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [origini, setOrigini] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [groupEditing, setGroupEditing] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [q, setQ] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [expiredOnly, setExpiredOnly] = useState(false);
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelReason, setCancelReason] = useState("prima_60");
  const [groupCancel, setGroupCancel] = useState(null);
  const [shiftTarget, setShiftTarget] = useState(null);
  const [shiftDays, setShiftDays] = useState("");
  const [invoiceDraft, setInvoiceDraft] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    api.get("/bookings").then((r) => setBookings(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/settings").then((r) => { setOrigini(r.data.origini || []); setAgencies(r.data.agencies_config || []); });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && bookings.length) {
      const b = bookings.find((x) => x.id === eid);
      if (b) { setEditing(b); setOpen(true); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, bookings]);

  useEffect(() => {
    const qid = searchParams.get("fromQuote");
    if (!qid) return;
    api.get(`/quotes/${qid}`).then(({ data: q }) => {
      const [first, ...rest] = (q.client_name || "").trim().split(/\s+/);
      setGroupEditing(null);
      setEditing({
        guest_name: first || "", guest_surname: rest.join(" "), checkin: q.checkin || "", checkout: q.checkout || "",
        adults: q.adulti || 2, children: q.bambini || 0, total_amount: q.totale_scontato ?? q.totale ?? 0,
        guest_phone: q.client_phone || "", status: "opzionata", notes: `Da preventivo del ${q.created_at?.slice(0, 10)} (${q.periodo_label})`,
      });
      setOpen(true);
      setSearchParams({}, { replace: true });
    }).catch(() => {});
  }, [searchParams]);

  const propName = (id) => properties.find((p) => p.id === id)?.name || "—";
  const propStruct = (id) => properties.find((p) => p.id === id)?.structure_name || "—";

  const monthChoices = [...new Set(bookings.map((b) => (b.checkin || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
  const _today = new Date().toISOString().slice(0, 10);
  const optionExpiry = (b) => {
    if ((b.status || "confermata") !== "opzionata") return null;
    if (b.option_expiry_date) return b.option_expiry_date.slice(0, 10);
    const days = parseInt(b.option_expiry_days || 0, 10);
    const ca = (b.created_at || "").slice(0, 10);
    if (!days || !ca) return null;
    const d = new Date(ca); d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const isOptionExpired = (b) => { const e = optionExpiry(b); return !!e && e < _today; };
  const filtered = bookings.filter((b) => {
    if (q.trim()) {
      const s = `${b.guest_name || ""} ${b.guest_surname || ""}`.toLowerCase();
      if (!s.includes(q.trim().toLowerCase())) return false;
    }
    if (expiredOnly && !isOptionExpired(b)) return false;
    if (monthFilter) {
      const [y, m] = monthFilter.split("-").map(Number);
      const mStart = `${monthFilter}-01`;
      const nxt = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
      if (!((b.checkin || "") < nxt && (b.checkout || "") > mStart)) return false;
    }
    return true;
  });

  const openNew = () => { setEditing(null); setGroupEditing(null); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setGroupEditing(null); setOpen(true); };
  const openGroupEdit = (g) => { setEditing(null); setGroupEditing(g); setOpen(true); };
  const onSaved = () => { setOpen(false); setGroupEditing(null); load(); };

  const sendDoc = (type, channel) => {
    if (!sendFor) return;
    window.open(`/${type}/${sendFor.id}?send=${channel}`, "_blank");
    setSendFor(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la prenotazione?")) return;
    await api.delete(`/bookings/${id}`);
    toast.success("Eliminata");
    load();
  };

  const doCancel = async () => {
    if (!cancelFor) return;
    try {
      await api.patch(`/bookings/${cancelFor.id}/cancel`, { reason: cancelReason });
      toast.success("Prenotazione cancellata");
      setCancelFor(null);
      load();
    } catch (e) { toast.error("Cancellazione non riuscita"); }
  };

  const doGroupCancel = async () => {
    if (!groupCancel) return;
    try {
      await api.patch(`/bookings/group/${groupCancel.group_id}/cancel`, { reason: cancelReason });
      toast.success("Gruppo cancellato");
      setGroupCancel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancellazione non riuscita"); }
  };

  const setGroupNoComm = async (g, val) => {
    try {
      await api.patch(`/bookings/group/${g.group_id}/no-commission`, { value: val });
      toast.success(val ? "Nessuna commissione applicata al gruppo" : "Commissione ripristinata sul gruppo");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };

  const removeGroup = async (g) => {    if (!window.confirm(`Eliminare definitivamente l'intero gruppo (${g.rooms.length} camere)? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/bookings/group/${g.group_id}`);
      toast.success("Gruppo eliminato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Eliminazione non riuscita"); }
  };

  const doShift = async () => {
    const n = parseInt(shiftDays, 10);
    if (!shiftTarget || !n) { toast.error("Inserisci un numero di giorni (anche negativo)"); return; }
    try {
      if (shiftTarget.isGroup) await api.patch(`/bookings/group/${shiftTarget.group_id}/shift`, { days: n });
      else await api.patch(`/bookings/${shiftTarget.id}/shift`, { days: n });
      toast.success(`Spostato di ${n > 0 ? "+" : ""}${n} giorni`);
      setShiftTarget(null); setShiftDays("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Spostamento non riuscito"); }
  };

  const restoreGroup = async (g) => {
    if (!window.confirm(`Ripristinare l'intero gruppo (${g.rooms.length} camere) riportandolo a Confermato?`)) return;
    try {
      await api.patch(`/bookings/group/${g.group_id}/restore`);
      toast.success("Gruppo ripristinato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Ripristino non riuscito"); }
  };

  const restoreBooking = async (b) => {
    if (!window.confirm("Ripristinare la prenotazione riportandola a Confermata?")) return;
    try {
      await api.patch(`/bookings/${b.id}/restore`);
      toast.success("Prenotazione ripristinata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Ripristino non riuscito"); }
  };

  // raggruppa per group_id mantenendo l'ordine
  const groupedRows = [];
  const seenG = {};
  filtered.forEach((b) => {
    if (b.group_id) {
      if (!seenG[b.group_id]) { seenG[b.group_id] = { group_id: b.group_id, rooms: [] }; groupedRows.push(seenG[b.group_id]); }
      seenG[b.group_id].rooms.push(b);
    } else groupedRows.push({ single: b });
  });

  const Actions = ({ b }) => (
    <div className="flex flex-wrap md:flex-nowrap gap-1 justify-end">
      <button data-testid={`send-${b.id}`} title="Invia" onClick={() => setSendFor(b)} className="p-2 md:p-1.5 text-slate-400 hover:text-green-600"><Send size={15} /></button>
      <button data-testid={`voucher-${b.id}`} title="Voucher" onClick={() => window.open(`/voucher/${b.id}`, "_blank")} className="p-2 md:p-1.5 text-slate-400 hover:text-sky-600"><FileText size={15} /></button>
      <button data-testid={`ricevuta-${b.id}`} title="Ricevuta" onClick={() => window.open(`/ricevuta/${b.id}`, "_blank")} className="p-2 md:p-1.5 text-slate-400 hover:text-emerald-600"><ReceiptText size={15} /></button>
      {isAdmin && <button data-testid={`invoice-${b.id}`} title="Nuova fattura" onClick={() => setInvoiceDraft(draftFromBookings([b]))} className="p-2 md:p-1.5 text-slate-400 hover:text-indigo-600"><FileSpreadsheet size={15} /></button>}
      <button data-testid={`edit-${b.id}`} title="Modifica" onClick={() => openEdit(b)} className="p-2 md:p-1.5 text-slate-400 hover:text-sky-600"><Pencil size={15} /></button>
      {b.status !== "cancellata" && <button data-testid={`shift-${b.id}`} title="Sposta date (N giorni)" onClick={() => { setShiftDays(""); setShiftTarget({ isGroup: false, id: b.id, name: `${b.guest_name} ${b.guest_surname}`, checkin: b.checkin, checkout: b.checkout }); }} className="p-2 md:p-1.5 text-slate-400 hover:text-violet-600"><CalendarClock size={15} /></button>}
      {b.status !== "cancellata" && <button data-testid={`cancel-${b.id}`} title="Cancella (con motivazione)" onClick={() => { setCancelReason("prima_60"); setCancelFor(b); }} className="p-2 md:p-1.5 text-slate-400 hover:text-orange-600"><Ban size={15} /></button>}
      {b.status === "cancellata" && <button data-testid={`restore-${b.id}`} title="Ripristina (annulla cancellazione)" onClick={() => restoreBooking(b)} className="p-2 md:p-1.5 text-slate-400 hover:text-emerald-600"><RotateCcw size={15} /></button>}
      <button data-testid={`del-${b.id}`} title="Elimina" onClick={() => remove(b.id)} className="p-2 md:p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
    </div>
  );

  const todayStr = new Date().toISOString().slice(0, 10);
  const isAccontoOverdue = (b) =>
    b.status !== "cancellata" && (b.payment_status || "none") === "none" &&
    !!b.acconto_due_date && b.acconto_due_date < todayStr;

  const BRow = ({ b, grouped }) => {
    const overdue = isAccontoOverdue(b);
    const optExpired = isOptionExpired(b);
    return (
    <tr data-testid={overdue ? `overdue-row-${b.id}` : undefined} className={`border-b border-slate-100 hover:bg-slate-50 ${optExpired ? "bg-amber-50/60 border-l-4 border-l-amber-400" : overdue ? "bg-rose-50 border-l-4 border-l-rose-400" : grouped ? "bg-sky-50/20" : ""}`}>
      <td className="px-3 py-2.5 font-medium text-slate-800">
        {grouped ? <span className="text-slate-400 pl-3">↳ {propName(b.property_id)}</span> : <>{b.guest_name} {b.guest_surname}</>}
        <StatusPill b={b} />
        {b.no_commission && <span data-testid={`no-comm-badge-${b.id}`} className="ml-1.5 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">No commissione</span>}
        {optExpired && <span data-testid={`opt-expired-${b.id}`} title={`Opzione scaduta il ${optionExpiry(b)}`} className="ml-1.5 align-middle inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300"><AlertTriangle size={10} /> Opzione scaduta</span>}
        {overdue && <span data-testid={`acconto-overdue-${b.id}`} title={`Acconto scaduto il ${b.acconto_due_date}`} className="ml-1.5 align-middle inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300"><AlertTriangle size={10} /> Acconto scaduto</span>}
      </td>
      <td className="px-3 py-2.5 text-slate-600">{grouped ? "" : propStruct(b.property_id)}</td>
      <td className="px-3 py-2.5 text-slate-600">{propName(b.property_id)}</td>
      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{b.checkin}{b.checkin_time && <div className="text-[11px] text-sky-700 font-semibold" data-testid={`sb-in-${b.id}`}>⛴ {b.checkin_time}</div>}</td>
      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{b.checkout}{b.checkout_time && <div className="text-[11px] text-sky-700 font-semibold" data-testid={`sb-out-${b.id}`}>⛴ {b.checkout_time}</div>}</td>
      <td className="px-3 py-2.5 text-slate-600">{b.origine}</td>
      <td className="px-3 py-2.5 text-right tabular font-medium">{eur(b.total_amount)}</td>
      <td className="px-3 py-2.5 text-right tabular text-emerald-600">{eur(b.acconto)}</td>
      <td className="px-3 py-2.5 text-right tabular text-amber-600">{eur(b.saldo)}</td>
      <td className="px-3 py-2.5 text-slate-600 text-xs leading-tight">Acc: {PAYMENT[b.acconto_method] || "—"}<br />Sal: {PAYMENT[b.saldo_method] || "—"}</td>
      <td className="px-3 py-2.5"><Actions b={b} /></td>
    </tr>
    );
  };

  const shortD = (d) => (d ? d.split("-").reverse().join("/") : "—");
  const BCard = ({ b, grouped }) => {
    const overdue = isAccontoOverdue(b);
    const optExpired = isOptionExpired(b);
    return (
      <div data-testid={`booking-card-${b.id}`} className={`p-3 border-b border-slate-100 ${optExpired ? "bg-amber-50/60 border-l-4 border-l-amber-400" : overdue ? "bg-rose-50 border-l-4 border-l-rose-400" : grouped ? "bg-sky-50/30 pl-5" : ""}`}>
        <div className="flex justify-between gap-2">
          <div className="font-medium text-slate-800 min-w-0">
            {grouped ? <span className="text-slate-500">↳ {propName(b.property_id)}</span> : <>{b.guest_name} {b.guest_surname}</>}
            <StatusPill b={b} />
            {optExpired && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">Opzione scaduta</span>}
            {overdue && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 whitespace-nowrap">Acconto scaduto</span>}
          </div>
          <div className="text-right tabular font-semibold text-slate-900 shrink-0">{eur(b.total_amount)}</div>
        </div>
        {!grouped && <div className="text-xs text-slate-500 mt-0.5">{propName(b.property_id)} · {propStruct(b.property_id)} · {b.origine}</div>}
        <div className="text-xs text-slate-600 mt-1">
          {shortD(b.checkin)}{b.checkin_time && <span className="text-sky-700 font-semibold"> ⛴ {b.checkin_time}</span>} → {shortD(b.checkout)}{b.checkout_time && <span className="text-sky-700 font-semibold"> ⛴ {b.checkout_time}</span>}
        </div>
        <div className="flex flex-wrap justify-between items-center gap-1 mt-1.5">
          <span className="text-xs tabular"><span className="text-emerald-600">Acc. {eur(b.acconto)}</span> · <span className="text-amber-600">Saldo {eur(b.saldo)}</span></span>
          <Actions b={b} />
        </div>
      </div>
    );
  };

  const GroupButtons = ({ g }) => (
                      <div className="flex flex-wrap gap-1 justify-end pr-2">
                        <button data-testid={`group-edit-${g.group_id}`} title="Modifica gruppo" onClick={() => openGroupEdit(g)} className="text-xs font-semibold px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Modifica gruppo</button>
                        {(() => { const allNc = g.rooms.every((r) => r.no_commission); return (
                          <button data-testid={`group-nocomm-${g.group_id}`} title="Applica/Rimuovi 'Nessuna commissione' a tutto il gruppo" onClick={() => setGroupNoComm(g, !allNc)} className={`text-xs font-semibold px-2 py-1 rounded-md border inline-flex items-center gap-1 ${allNc ? "border-indigo-300 bg-indigo-100 text-indigo-700" : "border-indigo-200 text-indigo-600 hover:bg-indigo-50"}`}>{allNc ? "✓ Nessuna commissione" : "Nessuna commissione"}</button>
                        ); })()}
                        <button data-testid={`group-shift-${g.group_id}`} title="Sposta le date di tutte le camere" onClick={() => { setShiftDays(""); setShiftTarget({ isGroup: true, group_id: g.group_id, count: g.rooms.length, checkin: g.rooms[0].checkin, checkout: g.rooms[0].checkout }); }} className="text-xs font-semibold px-2 py-1 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"><CalendarClock size={13} /> Sposta date</button>
                        <button data-testid={`group-voucher-${g.group_id}`} title="Voucher gruppo" onClick={() => window.open(`/voucher/group/${g.group_id}`, "_blank")} className="text-xs font-semibold px-2 py-1 rounded-md border border-sky-200 text-sky-700 hover:bg-sky-50">Voucher gruppo</button>
                        <button data-testid={`group-ricevuta-${g.group_id}`} title="Ricevuta gruppo" onClick={() => window.open(`/ricevuta/group/${g.group_id}`, "_blank")} className="text-xs font-semibold px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Ricevuta gruppo</button>
                        {g.rooms.some((r) => r.status !== "cancellata") && (
                          <button data-testid={`group-cancel-${g.group_id}`} title="Cancella tutto il gruppo (con motivazione)" onClick={() => { setCancelReason("prima_60"); setGroupCancel(g); }} className="text-xs font-semibold px-2 py-1 rounded-md border border-orange-200 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1"><Ban size={13} /> Cancella gruppo</button>
                        )}
                        {g.rooms.some((r) => r.status === "cancellata") && (
                          <button data-testid={`group-restore-${g.group_id}`} title="Ripristina il gruppo cancellato" onClick={() => restoreGroup(g)} className="text-xs font-semibold px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"><RotateCcw size={13} /> Ripristina gruppo</button>
                        )}
                        {isAdmin && <button data-testid={`group-invoice-${g.group_id}`} title="Nuova fattura per il gruppo" onClick={() => setInvoiceDraft(draftFromBookings(g.rooms))} className="text-xs font-semibold px-2 py-1 rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 inline-flex items-center gap-1"><FileSpreadsheet size={13} /> Nuova fattura</button>}
                        <button data-testid={`group-del-${g.group_id}`} title="Elimina definitivamente tutto il gruppo" onClick={() => removeGroup(g)} className="text-xs font-semibold px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={13} /> Elimina gruppo</button>
                      </div>
  );

  const exportCSV = () => {
    const headers = ["Ospite", "Camera", "Check-in", "Check-out", "Origine", "Totale", "Acconto", "Saldo", "Metodo"];
    const rows = bookings.map((b) => [
      `${b.guest_name} ${b.guest_surname}`, propName(b.property_id), b.checkin, b.checkout,
      b.origine, b.total_amount, b.acconto, b.saldo, PAYMENT[b.payment_method] || b.payment_method,
    ]);
    downloadCSV(`prenotazioni_${today()}.csv`, headers, rows);
  };

  return (
    <div>
      <PageHeader
        title={isAdmin ? "Prenotazioni" : "Le mie prenotazioni"}
        subtitle={isAdmin ? "Prenotazioni, incassi e pagamenti" : "Inserisci e gestisci le tue prenotazioni"}
      >
        <ExportButton onClick={exportCSV} testid="export-bookings" />
        <Btn data-testid="new-booking-btn" onClick={openNew}>
          <Plus size={16} /> Nuova prenotazione
        </Btn>
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          data-testid="money-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome o cognome ospite…"
          className="w-full sm:w-72 px-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
        />
        <select
          data-testid="money-month-filter"
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-200 font-medium"
        >
          <option value="">Tutti i mesi</option>
          {monthChoices.map((m) => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>
        {(q.trim() || monthFilter) && <span className="text-xs text-slate-500">{filtered.length} risultati{monthFilter ? ` · ${monthLabel(monthFilter)}` : ""}</span>}
        <button
          data-testid="money-expired-filter"
          onClick={() => setExpiredOnly((v) => !v)}
          className={`ml-auto text-xs font-semibold px-3 py-2 rounded-md border inline-flex items-center gap-1.5 ${expiredOnly ? "bg-rose-600 text-white border-rose-600" : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"}`}
        >
          <AlertTriangle size={14} /> {expiredOnly ? "Mostra tutte" : "Solo opzioni scadute"}
        </button>
      </div>

      <div className="md:hidden bg-white border border-slate-200 rounded-md" data-testid="bookings-cards">
        {groupedRows.map((entry) => {
          if (entry.single) return <BCard key={entry.single.id} b={entry.single} />;
          const g = entry;
          const first = g.rooms[0];
          return (
            <div key={g.group_id} className="border-b border-slate-200">
              <div className="p-3 bg-sky-100/50" data-testid={`group-card-${g.group_id}`}>
                <div className="flex justify-between gap-2">
                  <b className="text-slate-800">{first.guest_name} {first.guest_surname}</b>
                  <b className="tabular">{eur(g.rooms.reduce((a, r) => a + Number(r.total_amount || 0), 0))}</b>
                </div>
                <div className="text-xs text-slate-600 mb-2">Gruppo · {g.rooms.length} camere · {shortD(first.checkin)} → {shortD(first.checkout)} · {first.origine}</div>
                <GroupButtons g={g} />
              </div>
              {g.rooms.map((b) => <BCard key={b.id} b={b} grouped />)}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="px-3 py-12 text-center text-slate-400">{q.trim() ? "Nessun risultato" : "Nessuna prenotazione"}</div>}
      </div>

      <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5">Ospite</th>
              <th className="px-3 py-2.5">Proprietà</th>
              <th className="px-3 py-2.5">Camera</th>
              <th className="px-3 py-2.5">Check-in</th>
              <th className="px-3 py-2.5">Check-out</th>
              <th className="px-3 py-2.5">Origine</th>
              <th className="px-3 py-2.5 text-right">Totale</th>
              <th className="px-3 py-2.5 text-right">Acconto</th>
              <th className="px-3 py-2.5 text-right">Saldo</th>
              <th className="px-3 py-2.5">Metodo</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody data-testid="bookings-table">
            {groupedRows.map((entry) => {
              if (entry.single) return <BRow key={entry.single.id} b={entry.single} />;
              const g = entry;
              const first = g.rooms[0];
              const total = g.rooms.reduce((a, r) => a + Number(r.total_amount || 0), 0);
              return (
                <Fragment key={g.group_id}>
                  <tr className="border-b border-slate-200 bg-sky-100/50" data-testid={`group-row-${g.group_id}`}>
                    <td className="px-3 py-2.5 font-bold text-slate-800">
                      {first.guest_name} {first.guest_surname}
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-200 text-sky-800">Gruppo · {g.rooms.length} camere</span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 text-xs" colSpan={2}>{first.origine}</td>
                    <td className="px-3 py-2.5 text-slate-600">{first.checkin}{first.checkin_time && <div className="text-[11px] text-sky-700 font-semibold">⛴ {first.checkin_time}</div>}</td>
                    <td className="px-3 py-2.5 text-slate-600">{first.checkout}{first.checkout_time && <div className="text-[11px] text-sky-700 font-semibold">⛴ {first.checkout_time}</div>}</td>
                    <td className="px-3 py-2.5"></td>
                    <td className="px-3 py-2.5 text-right tabular font-bold">{eur(total)}</td>
                    <td colSpan={4}>
                      <div className="flex flex-wrap gap-1 justify-end pr-2">
                        <button data-testid={`group-edit-${g.group_id}`} title="Modifica gruppo" onClick={() => openGroupEdit(g)} className="text-xs font-semibold px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Modifica gruppo</button>
                        {(() => { const allNc = g.rooms.every((r) => r.no_commission); return (
                          <button data-testid={`group-nocomm-${g.group_id}`} title="Applica/Rimuovi 'Nessuna commissione' a tutto il gruppo" onClick={() => setGroupNoComm(g, !allNc)} className={`text-xs font-semibold px-2 py-1 rounded-md border inline-flex items-center gap-1 ${allNc ? "border-indigo-300 bg-indigo-100 text-indigo-700" : "border-indigo-200 text-indigo-600 hover:bg-indigo-50"}`}>{allNc ? "✓ Nessuna commissione" : "Nessuna commissione"}</button>
                        ); })()}
                        <button data-testid={`group-shift-${g.group_id}`} title="Sposta le date di tutte le camere" onClick={() => { setShiftDays(""); setShiftTarget({ isGroup: true, group_id: g.group_id, count: g.rooms.length, checkin: g.rooms[0].checkin, checkout: g.rooms[0].checkout }); }} className="text-xs font-semibold px-2 py-1 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"><CalendarClock size={13} /> Sposta date</button>
                        <button data-testid={`group-voucher-${g.group_id}`} title="Voucher gruppo" onClick={() => window.open(`/voucher/group/${g.group_id}`, "_blank")} className="text-xs font-semibold px-2 py-1 rounded-md border border-sky-200 text-sky-700 hover:bg-sky-50">Voucher gruppo</button>
                        <button data-testid={`group-ricevuta-${g.group_id}`} title="Ricevuta gruppo" onClick={() => window.open(`/ricevuta/group/${g.group_id}`, "_blank")} className="text-xs font-semibold px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Ricevuta gruppo</button>
                        {g.rooms.some((r) => r.status !== "cancellata") && (
                          <button data-testid={`group-cancel-${g.group_id}`} title="Cancella tutto il gruppo (con motivazione)" onClick={() => { setCancelReason("prima_60"); setGroupCancel(g); }} className="text-xs font-semibold px-2 py-1 rounded-md border border-orange-200 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1"><Ban size={13} /> Cancella gruppo</button>
                        )}
                        {g.rooms.some((r) => r.status === "cancellata") && (
                          <button data-testid={`group-restore-${g.group_id}`} title="Ripristina il gruppo cancellato" onClick={() => restoreGroup(g)} className="text-xs font-semibold px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"><RotateCcw size={13} /> Ripristina gruppo</button>
                        )}
                        {isAdmin && <button data-testid={`group-invoice-${g.group_id}`} title="Nuova fattura per il gruppo" onClick={() => setInvoiceDraft(draftFromBookings(g.rooms))} className="text-xs font-semibold px-2 py-1 rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 inline-flex items-center gap-1"><FileSpreadsheet size={13} /> Nuova fattura</button>}
                        <button data-testid={`group-del-${g.group_id}`} title="Elimina definitivamente tutto il gruppo" onClick={() => removeGroup(g)} className="text-xs font-semibold px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={13} /> Elimina gruppo</button>
                      </div>
                    </td>
                  </tr>
                  {g.rooms.map((b) => <BRow key={b.id} b={b} grouped />)}
                </Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-12 text-center text-slate-400"><Wallet className="mx-auto mb-2" /> {q.trim() ? "Nessun risultato" : "Nessuna prenotazione"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setGroupEditing(null); }} title={groupEditing ? "Modifica gruppo" : editing?.id ? "Modifica prenotazione" : "Nuova prenotazione"} wide>
        {open && (
          <BookingForm
            properties={properties}
            origini={origini}
            agencies={agencies}
            isAdmin={isAdmin}
            user={user}
            initial={editing}
            groupEdit={groupEditing}
            onSaved={onSaved}
            onCancel={() => { setOpen(false); setGroupEditing(null); }}
          />
        )}
      </Modal>

      <Modal open={!!invoiceDraft} onClose={() => setInvoiceDraft(null)} title="Nuova fattura" wide>
        {invoiceDraft && <InvoiceForm initial={invoiceDraft} onCancel={() => setInvoiceDraft(null)} onSaved={(inv) => { setInvoiceDraft(null); window.open(`/fattura/${inv.id}`, "_blank"); }} />}
      </Modal>

      <Modal open={!!sendFor} onClose={() => setSendFor(null)} title="Invia documento all'ospite">
        {sendFor && (
          <div className="space-y-4" data-testid="send-doc-modal">
            <p className="text-sm text-slate-500">
              {sendFor.guest_name} {sendFor.guest_surname} · {sendFor.guest_email || "email non impostata"}{sendFor.guest_phone ? ` · ${sendFor.guest_phone}` : ""}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Btn variant="secondary" data-testid="m-send-voucher-email" onClick={() => sendDoc("voucher", "email")}><Mail size={15} /> Voucher · Email</Btn>
              <Btn variant="secondary" data-testid="m-send-voucher-whatsapp" onClick={() => sendDoc("voucher", "whatsapp")}><MessageCircle size={15} /> Voucher · WhatsApp</Btn>
              <Btn variant="secondary" data-testid="m-send-ricevuta-email" onClick={() => sendDoc("ricevuta", "email")}><Mail size={15} /> Ricevuta · Email</Btn>
              <Btn variant="secondary" data-testid="m-send-ricevuta-whatsapp" onClick={() => sendDoc("ricevuta", "whatsapp")}><MessageCircle size={15} /> Ricevuta · WhatsApp</Btn>
            </div>
            <p className="text-xs text-slate-400">Il documento si apre in una nuova scheda e l'invio parte automaticamente. Per l'email serve l'indirizzo ospite (altrimenti verrà richiesto).</p>
          </div>
        )}
      </Modal>
      <Modal open={!!cancelFor} onClose={() => setCancelFor(null)} title="Cancella prenotazione">
        {cancelFor && (
          <div className="space-y-4" data-testid="cancel-modal">
            <p className="text-sm text-slate-600">
              Vuoi cancellare la prenotazione di <b>{cancelFor.guest_name} {cancelFor.guest_surname}</b> — camera <b>{propName(cancelFor.property_id)}</b> ({cancelFor.checkin} → {cancelFor.checkout})?
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Motivazione</label>
              <select data-testid="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500">
                {CANCEL_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="secondary" data-testid="cancel-abort" onClick={() => setCancelFor(null)}>Annulla</Btn>
              <Btn data-testid="cancel-confirm" onClick={doCancel}>Conferma cancellazione</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!groupCancel} onClose={() => setGroupCancel(null)} title="Cancella gruppo">
        {groupCancel && (
          <div className="space-y-4" data-testid="group-cancel-modal">
            <p className="text-sm text-slate-600">
              Vuoi cancellare <b>tutte le {groupCancel.rooms.length} camere</b> del gruppo di <b>{groupCancel.rooms[0].guest_name} {groupCancel.rooms[0].guest_surname}</b>? La stessa motivazione verrà applicata a tutte.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Motivazione</label>
              <select data-testid="group-cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500">
                {CANCEL_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="secondary" data-testid="group-cancel-abort" onClick={() => setGroupCancel(null)}>Annulla</Btn>
              <Btn data-testid="group-cancel-confirm" onClick={doGroupCancel}>Cancella tutto il gruppo</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!shiftTarget} onClose={() => setShiftTarget(null)} title={shiftTarget?.isGroup ? "Sposta date del gruppo" : "Sposta date prenotazione"}>
        {shiftTarget && (
          <div className="space-y-4" data-testid="shift-modal">
            <p className="text-sm text-slate-600">
              {shiftTarget.isGroup
                ? <>Sposta check-in e check-out di <b>tutte le {shiftTarget.count} camere</b> di un numero di giorni. Usa un valore negativo per anticipare.</>
                : <>Sposta check-in e check-out di <b>{shiftTarget.name}</b> di un numero di giorni. Usa un valore negativo per anticipare.</>}
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Giorni di spostamento</label>
              <input data-testid="shift-days" type="number" value={shiftDays} onChange={(e) => setShiftDays(e.target.value)} placeholder="Es. 7 oppure -3" className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500" />
              <p className="text-xs text-slate-400 mt-1">Attuale: {shiftTarget.checkin} → {shiftTarget.checkout}</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="secondary" data-testid="shift-abort" onClick={() => setShiftTarget(null)}>Annulla</Btn>
              <Btn data-testid="shift-confirm" onClick={doShift}>Sposta</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function StatusPill({ b }) {
  const st = b.status || "confermata";
  if (st === "opzionata") return <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">Opzionata</span>;
  if (st === "cancellata") return <span className="inline-block ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">Cancellata{b.cancellation ? ` · ${CANCEL_LABEL[b.cancellation.reason]?.split(" (")[0] || b.cancellation.reason} · voucher ${b.cancellation.voucher_percent}%` : ""}</span>;
  const ps = b.payment_status || "none";
  const map = { none: ["bg-orange-100 text-orange-700", "Da incassare"], partial: ["bg-amber-100 text-amber-700", "Acconto"], full: ["bg-emerald-100 text-emerald-700", "Saldato"] };
  const [cls, lbl] = map[ps] || map.none;
  return <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{lbl}</span>;
}
